import { useState } from 'react'
import {
  useAdminUsers,
  useAdminCreateUser,
  useAdminUpdateUser,
  useAdminDeleteUser,
  useAdminRestoreUser,
} from '../../hooks/useAdmin'
import { useAuthStore } from '../../store/auth'
import { ConfirmModal } from '../../components/ui/ConfirmModal'
import { AdminModal, adminFieldClass } from '../../components/admin/AdminModal'
import type { AdminUser } from '../../types/admin'

const ROLES = ['person', 'church_admin', 'super_admin'] as const

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const roleBadge: Record<string, { bg: string; color: string }> = {
  super_admin: { bg: '#1a1a2e', color: '#fff' },
  church_admin: { bg: 'var(--primary-soft)', color: 'var(--primary)' },
  person: { bg: 'var(--surface-2)', color: 'var(--ink-2)' },
}

type UserFormState = {
  email: string
  displayName: string
  password: string
  role: string
}

const emptyForm = (): UserFormState => ({
  email: '',
  displayName: '',
  password: '',
  role: 'person',
})

export default function AdminUsersPage() {
  const { user: me } = useAuthStore()
  const [q, setQ] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [confirmDelete, setConfirmDelete] = useState<AdminUser | null>(null)
  const [editing, setEditing] = useState<AdminUser | 'new' | null>(null)
  const [form, setForm] = useState<UserFormState>(emptyForm)
  const [showDeleted, setShowDeleted] = useState(false)

  const { data, isLoading } = useAdminUsers({ q: search || undefined, page, showDeleted })
  const createUser = useAdminCreateUser()
  const updateUser = useAdminUpdateUser()
  const deleteUser = useAdminDeleteUser()
  const restoreUser = useAdminRestoreUser()

  const users = data?.data ?? []
  const total = data?.meta.total ?? 0
  const totalPages = Math.ceil(total / 20)

  function openCreate() {
    setForm(emptyForm())
    setEditing('new')
  }

  function openEdit(user: AdminUser) {
    setForm({
      email: user.email,
      displayName: user.displayName,
      password: '',
      role: user.role,
    })
    setEditing(user)
  }

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    setSearch(q)
    setPage(1)
  }

  function handleSave() {
    if (editing === 'new') {
      createUser.mutate(
        {
          email: form.email.trim(),
          password: form.password,
          displayName: form.displayName.trim(),
          role: form.role,
        },
        { onSuccess: () => setEditing(null) },
      )
      return
    }
    if (!editing) return
    const body: { displayName: string; email: string; role: string; password?: string } = {
      displayName: form.displayName.trim(),
      email: form.email.trim(),
      role: form.role,
    }
    if (form.password) body.password = form.password
    updateUser.mutate({ id: editing.id, ...body }, { onSuccess: () => setEditing(null) })
  }

  const isPending = createUser.isPending || updateUser.isPending

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="m-0 text-[24px] font-bold">Users</h1>
          <div className="text-[13px] text-muted mt-1">{total} total</div>
        </div>

        <div className="flex gap-2 items-center">
          <form onSubmit={handleSearch} className="flex gap-2">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name or email…"
              aria-label="Search users"
              className="h-9 px-3 rounded-lg border border-line-soft bg-surface text-[13px] font-sans text-ink outline-none w-60"
            />
            <button
              type="submit"
              className="h-9 px-3.5 rounded-lg border-0 bg-primary text-on-primary text-[13px] font-semibold cursor-pointer font-sans"
            >
              Search
            </button>
          </form>
          <button
            type="button"
            onClick={() => { setShowDeleted((v) => !v); setPage(1) }}
            className={`h-9 px-3.5 rounded-lg border text-[13px] font-semibold cursor-pointer font-sans ${showDeleted ? 'bg-amber-50 border-amber-300 text-amber-800' : 'border-line-soft bg-surface text-ink-2'}`}
          >
            {showDeleted ? 'Hide deleted' : 'Show deleted'}
          </button>
          {!showDeleted && (
            <button
              type="button"
              onClick={openCreate}
              className="h-9 px-4 rounded-lg border-0 bg-[#1a1a2e] text-white text-[13px] font-semibold cursor-pointer font-sans"
            >
              + Add user
            </button>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="text-muted text-[14px] py-10">Loading…</div>
      ) : (
        <div className="bg-surface rounded-[14px] border border-line-soft overflow-hidden">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr className="border-b border-line-soft bg-s2">
                {['Name', 'Email', 'Role', 'Joined', 'Actions'].map((h) => (
                  <th key={h} className="px-4 py-2.5 text-left font-semibold text-ink-2 text-[12px]">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => {
                const isSelf = u.id === me?.id
                const badge = roleBadge[u.role] ?? roleBadge.person
                return (
                  <tr key={u.id} className={i < users.length - 1 ? 'border-b border-line-soft' : ''}>
                    <td className="px-4 py-3 font-medium">{u.displayName}</td>
                    <td className="px-4 py-3 text-ink-2">{u.email}</td>
                    <td className="px-4 py-3">
                      <span
                        className="px-2 py-0.5 rounded-md text-[11px] font-semibold"
                        style={{ background: badge.bg, color: badge.color }}
                      >
                        {u.role}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted">{formatDate(u.createdAt)}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        {u.deletedAt ? (
                          <button
                            type="button"
                            onClick={() => restoreUser.mutate(u.id)}
                            disabled={restoreUser.isPending}
                            className="px-2.5 py-1 rounded-md text-[12px] cursor-pointer font-sans border border-good bg-good-soft text-good-deep disabled:opacity-50"
                          >
                            Restore
                          </button>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={() => openEdit(u)}
                              className="px-2.5 py-1 rounded-md text-[12px] cursor-pointer font-sans border border-line-soft bg-s2 text-ink"
                            >
                              Edit
                            </button>
                            {!isSelf && (
                              <button
                                type="button"
                                onClick={() => setConfirmDelete(u)}
                                className="px-2.5 py-1 rounded-md text-[12px] cursor-pointer font-sans border border-danger-line bg-danger-soft text-danger-deep"
                              >
                                Delete
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex gap-2 mt-5 justify-end">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1.5 rounded-lg border border-line-soft bg-surface text-ink text-[13px] cursor-pointer font-sans disabled:opacity-40">← Prev</button>
          <span className="text-[13px] text-muted self-center">Page {page} of {totalPages}</span>
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1.5 rounded-lg border border-line-soft bg-surface text-ink text-[13px] cursor-pointer font-sans disabled:opacity-40">Next →</button>
        </div>
      )}

      {editing && (
        <AdminModal
          title={editing === 'new' ? 'Add user' : 'Edit user'}
          onClose={() => setEditing(null)}
          footer={
            <>
              <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 rounded-lg border border-line-soft bg-surface text-[13px] cursor-pointer font-sans">
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isPending || !form.email || !form.displayName || (editing === 'new' && !form.password)}
                className="px-4 py-2 rounded-lg border-0 bg-[#1a1a2e] text-white text-[13px] font-semibold cursor-pointer font-sans disabled:opacity-50"
              >
                {isPending ? 'Saving…' : 'Save'}
              </button>
            </>
          }
        >
          {editing !== 'new' && (
            <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
              UUID
              <div className="flex gap-1.5">
                <input
                  readOnly
                  value={editing.id}
                  className={`${adminFieldClass()} flex-1 font-mono text-[11px] text-muted cursor-text select-all`}
                />
                <button
                  type="button"
                  onClick={() => void navigator.clipboard.writeText(editing.id)}
                  className="px-2.5 py-1.5 rounded-lg border border-line-soft bg-s2 text-[11px] text-ink-2 cursor-pointer font-sans shrink-0"
                >
                  Copy
                </button>
              </div>
            </label>
          )}
          <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
            Display name
            <input className={adminFieldClass()} value={form.displayName} onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))} />
          </label>
          <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
            Email
            <input type="email" className={adminFieldClass()} value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} disabled={editing !== 'new' && editing.id === me?.id} />
          </label>
          <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
            Password {editing !== 'new' && <span className="font-normal text-muted">(leave blank to keep)</span>}
            <input type="password" className={adminFieldClass()} value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} />
          </label>
          <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
            Role
            <select className={adminFieldClass()} value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))} disabled={editing !== 'new' && editing.id === me?.id}>
              {ROLES.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </label>
        </AdminModal>
      )}

      {confirmDelete && (
        <ConfirmModal
          title="Delete user?"
          body={<><strong>{confirmDelete.displayName}</strong> ({confirmDelete.email}) will be soft-deleted. They won&apos;t be able to log in, but can be restored from the deleted users view.</>}
          confirmLabel="Delete"
          pendingLabel="Deleting…"
          onConfirm={() => deleteUser.mutate(confirmDelete.id, { onSuccess: () => setConfirmDelete(null) })}
          onCancel={() => setConfirmDelete(null)}
          isPending={deleteUser.isPending}
        />
      )}
    </div>
  )
}
